import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Layers, PackageMinus, Pencil } from 'lucide-react'

import {
    PurchaseLotStatusBadge,
    purchaseLotOutReasonLabel,
} from '@/components/purchase-lots/purchase-lot-badges'
import { PurchaseLotNoteActions } from '@/components/purchase-lots/purchase-lot-note-actions'
import { PurchaseLotNoteItemsTable } from '@/components/purchase-lots/purchase-lot-note-items-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import {
    useApprovePurchaseOutNote,
    useCancelPurchaseOutNote,
    usePurchaseOutNote,
    useRejectPurchaseOutNote,
} from '@/hooks/use-purchase-lot-query'
import { useSortingInNotes } from '@/hooks/use-sorting-lot-query'
import { formatDateTimeVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/stock-out-notes/$id',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const { data, isLoading } = usePurchaseOutNote(id)
    const note = data?.data
    const { user, hasPermission } = useAuth()
    const approve = useApprovePurchaseOutNote()
    const reject = useRejectPurchaseOutNote()
    const cancel = useCancelPurchaseOutNote()

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = user?.id && note.created_by_id === user.id
    const canApprove = hasPermission?.('purchase_lots.approve_out') ?? true
    const canWrite = hasPermission?.('purchase_lots.write') ?? true
    const isApproved = note.status === 'approved'
    const isForSorting = note.reason === 'for_sorting'
    // PXK đã có PNK tiếp nhận thì ẩn nút — form tạo sẽ luôn bị backend từ chối
    // (1 PXK = 1 PNK).
    const alreadyReceived = note.has_active_in_note === true
    const canCreateSortingPNK =
        isApproved &&
        isForSorting &&
        !alreadyReceived &&
        (hasPermission?.('sorting_lots.write') ?? true)

    return (
        <div className="space-y-4 p-4 md:p-6">
            <Button
                asChild
                variant="ghost"
                className="h-11 md:h-9 -ml-2 md:ml-0"
            >
                <Link to="/purchase-lots/stock-out-notes" search={{ page: 1 }}>
                    <ArrowLeft className="size-4 mr-1" />
                    {m.sl_lot_back()}
                </Link>
            </Button>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <PackageMinus className="size-5" />
                            <CardTitle className="font-mono text-base md:text-2xl">
                                {note.code}
                            </CardTitle>
                            <PurchaseLotStatusBadge status={note.status} />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {note.status === 'pending' &&
                                !!isCreator &&
                                canWrite &&
                                !note.stocktake_session_id && (
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="h-11 md:h-9"
                                    >
                                        <Link
                                            to="/purchase-lots/stock-out-notes/$id/edit"
                                            params={{ id }}
                                        >
                                            <Pencil className="size-4 mr-1" />
                                            {m.pl_pnk_edit_btn()}
                                        </Link>
                                    </Button>
                                )}
                            <PurchaseLotNoteActions
                                status={note.status}
                                canApprove={canApprove}
                                canCancel={!!isCreator && canWrite}
                                approvePending={approve.isPending}
                                rejectPending={reject.isPending}
                                cancelPending={cancel.isPending}
                                approveConfirm={m.pl_pxk_confirm_approve()}
                                cancelConfirm={m.pl_pxk_confirm_cancel()}
                                rejectTitle={m.pl_pxk_reject_title()}
                                onApprove={() => approve.mutateAsync(id)}
                                onReject={(reason) =>
                                    reject.mutateAsync({ id, reason })
                                }
                                onCancel={() => cancel.mutateAsync(id)}
                            />
                            {canCreateSortingPNK && (
                                <Button asChild className="h-11 md:h-9">
                                    <Link
                                        to="/sorting-warehouse/stock-in-notes/new"
                                        search={{ pxk: note.id }}
                                    >
                                        <Layers className="size-4 mr-1" />
                                        {m.pl_out_action_create_sorting_pnk()}
                                    </Link>
                                </Button>
                            )}
                            {alreadyReceived && (
                                <p className="text-sm text-muted-foreground self-center">
                                    {m.pxk_already_received_note()}
                                </p>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <div className="col-span-2">
                        <Field
                            label={m.sl_detail_label_reason()}
                            value={purchaseLotOutReasonLabel(note.reason)}
                        />
                    </div>
                    <Field
                        label={m.sl_detail_label_created_at()}
                        value={formatDateTimeVN(note.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={note.created_by_name ?? '—'}
                    />
                    {note.approved_at && (
                        <>
                            <Field
                                label={m.sl_detail_label_approved_at()}
                                value={formatDateTimeVN(note.approved_at)}
                            />
                            <Field
                                label={m.sl_detail_label_approver()}
                                value={note.approved_by_name ?? '—'}
                            />
                        </>
                    )}
                    {note.cancelled_at && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_cancelled_at()}
                                value={formatDateTimeVN(note.cancelled_at)}
                            />
                        </div>
                    )}
                    {note.notes && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_notes()}
                                value={note.notes}
                            />
                        </div>
                    )}
                    {note.rejected_reason && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_rejected_reason()}
                                value={note.rejected_reason}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            <PurchaseLotNoteItemsTable items={note.items ?? []} />

            {isForSorting && <SortingNotesFromPXK pxkId={note.id} />}
        </div>
    )
}

/**
 * Các PNK lô phân loại đã tạo từ PXK này. Vì nghiệp vụ KHÔNG cân sổ (tổng KL
 * nhập có thể khác KL xuất), đây là chỗ để người vận hành tự đối chiếu bằng mắt.
 */
function SortingNotesFromPXK({ pxkId }: { pxkId: string }) {
    const { data, isLoading } = useSortingInNotes({
        purchase_lot_stock_out_note_id: pxkId,
        per_page: 100,
    })
    const notes = data?.data ?? []

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">
                    {m.pl_pxk_sorting_notes_title()}
                </CardTitle>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <p className="text-sm text-muted-foreground">
                        {m.sl_detail_loading()}
                    </p>
                ) : notes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        {m.pl_pxk_sorting_notes_empty()}
                    </p>
                ) : (
                    <ul className="divide-y">
                        {notes.map((n) => (
                            <li
                                key={n.id}
                                className="flex items-center justify-between gap-3 py-2"
                            >
                                <Link
                                    to="/sorting-warehouse/stock-in-notes/$id"
                                    params={{ id: n.id }}
                                    className="font-mono text-sm hover:underline"
                                >
                                    {n.code}
                                </Link>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-muted-foreground">
                                        {formatDateTimeVN(n.created_at)}
                                    </span>
                                    <PurchaseLotStatusBadge status={n.status} />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    )
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
